/* eslint-disable prefer-reflect, no-magic-numbers, curly, id-length, max-lines, no-unused-expressions */
import * as d3 from 'd3';
import ResizeTracker from 'utils/ResizeTracker';

/**
* Base options
*/

const _options = {
    target: null,
    width: 328,
    height: 328,
    outerRadius: 0,
    innerRadius: 0,
    cornerRadius: 10,
    chartWidthFactor: 1.75,
    type: 'regular',
    bubblePath: 'M121.885 37.305c4.015 0 7.873 1.559 10.76 4.348l2.422 2.34a2.999 2.999 0 004.163.004l2.438-2.344a15.573 15.573 0 0110.795-4.348'
};


/**
 * @module Chart
 */
class Chart {

    /**
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - externally added options
     */

    constructor(element, options) {
        this._element = element;
        this._chart = null;
        this._target = this._element.querySelector('.chart--pie');

        const opt = { ...options, ..._options };
        this._state = {
            charts: [],
            arc: null,
            arcs: null,
            svg: null,
            slices: null,
            index: 0,
            colors: [],
            monthly: true,
            fontsize: 12,
            id: null,
            desc: null,
            descriptionText: ''
        };

        this._createLegends = this._createLegends.bind(this);
        this._pathHover = this._pathHover.bind(this);
        this._pathOut = this._pathOut.bind(this);
        this._toggleSvgCallback = this._toggleSvgCallback.bind(this);
        this._handleResize = this._handleResize.bind(this);

        this._options = opt;

        this._initialize();

    }

    /**
     * Initialize the chart
     *
     * @memberof Chart
     */

    _initialize() {
        this._setOptions();
        this._cacheSelectors();
        this._generateCharts();
        this._addListeners();
    }

    _addListeners() {
        window.addEventListener('initchart', this._handleResize);
    }

    /**
     * Set the chart options
     *
     * @memberof Chart
     */

    _setOptions() {
        this._state.id = this._element.getAttribute('id');
        const halfHeight = this._options.height / 2;
        const outerRadius = halfHeight;
        this._options.outerRadius = outerRadius;
        const innerRadius = outerRadius / this._options.chartWidthFactor;
        this._options.innerRadius = innerRadius;
    }

    /**
     * Cache selectors
     *
     * @private
     */

    _cacheSelectors() {
        const parent = this._options.parent ? this._options.parent : '.financial-overview__section';
        this._elements = {
            body: document.body,
            chartContainers: this._element.closest(parent) && [...this._element.closest(parent).querySelectorAll('.financial-overview__timeline')]
        };
        this._title = this._element.getAttribute('data-title');
    }

    /**
     * Generate the charts
     *
     * @private
     */

    _generateCharts() {

        !this._elements.chartContainers && this._createChartData(this._element, this._state.index);
        this._elements.chartContainers && this._elements.chartContainers.forEach((container, index) => this._createChartData(container, index));
        this._initializeChart();
        this._resizeTracker = new ResizeTracker();

        this._resizeTracker.init(
            { element: this._rootContainer },
            this._handleResize
        );
    }

    /**
     * Create the charts data
     *
     * @private
     */

    _createChartData(container, index) {
        // Get data from multiple or single components
        const rawData = JSON.parse(container.getAttribute('data-chart')) || {
            pensions: JSON.parse(container.getAttribute('data-data')),
            labels: JSON.parse(container.getAttribute('data-labels'))
        };
        this._controls = this._options.control && [...container.querySelectorAll(this._options.control)];
        const labelsContainer = container.querySelector('.timeline__providers') || container;
        const labels = JSON.parse(labelsContainer.getAttribute('data-chart-labels')) || rawData.labels;

        // Prepare the data from the pensions array or from the controls
        const controlledData = this._controls ? this._controls.map((element, ind) => {
            const isAow = element.getAttribute('data-aow') === 'true';

            const elementData = {
                name: element.getAttribute('data-chart-name'),
                value: parseInt(element.getAttribute('data-chart-value'), 10),
                controlLabel: isAow ? element.closest('.timeline__provider') : element,
                index: ind
            };
            return elementData;
        }) : rawData.pensions;

        // Sort the data from higher to lower value
        const sortedData = controlledData.sort((dataIn, dataOut) => parseFloat(dataOut.value) - parseFloat(dataIn.value));

        // Create the final chart object
        this._state.charts.push({
            data: sortedData.map(data => data.value),
            legendItems: sortedData.map(data => data.name),
            labels: labels ? labels : rawData.labels,
            controlLabel: sortedData.map(data => data.controlLabel),
            index: sortedData.map(data => data.index),
            title: rawData.title || container.getAttribute('data-title'),
            descriptionTotal: rawData.descriptionTotal || container.getAttribute('data-description-total'),
            descriptionPuv: rawData.descriptionPuv || container.getAttribute('data-description-puv')
        });

        // Handle period switch
        const hasNoSwitch = this._options.monthly === 'false';
        const hasPeriodSwitch = rawData.labels.title.monthly && rawData.labels.title.monthly !== '' && rawData.labels.title.yearly && rawData.labels.title.yearly !== '';
        hasPeriodSwitch && !hasNoSwitch && this._setupMutationObserver();
        this._state.monthly = hasNoSwitch ? false : this._state.monthly;

        // Handle triggers
        const triggers = [...container.querySelectorAll('.timeline__header button[data-trigger]')];
        triggers && triggers.forEach(trigger => {
            trigger.addEventListener('click', () => {
                // Set the cart index
                this._state.index = index;
                // Switch data
                this._switchChartData();
            });
        });

        // Handle control labels
        if (!this._state.charts[index].controlLabel[0]) return;
        this._state.charts[index].controlLabel.forEach((label, ind) => {
            label.addEventListener('mouseenter', () => this._pathHover(ind));
            label.addEventListener('mouseleave', () => this._pathOut(ind));
        });
    }

    /**
     * Initialize the SVG chart
     *
     * @memberof Chart
     */

    _initializeChart() {
        // Define variables
        const pie = d3.pie().value(d => d)
            .sort(null);
        this._state.pie = pie;
        this._state.arc = d3.arc()
            .outerRadius(this._options.outerRadius - 10)
            .innerRadius(this._options.innerRadius);

        this._state.chart = this._state.charts[this._state.index];
        this._state.arcs = pie(this._state.chart.data);

        const svg = d3.select(this._target).append('svg')
            .attr('viewBox', [-this._options.width / 2, -this._options.height / 2, this._options.width, this._options.height]);
        this._state.svg = svg;
        const periodTitle = this._state.monthly ? this._state.chart.labels.title.monthly : this._state.chart.labels.title.yearly;
        const periodsubtitle = this._state.monthly ? this._state.chart.labels.subtitle.monthly : this._state.chart.labels.subtitle.yearly;
        this._state.descriptionText = `${this._state.chart.descriptionTotal} ${periodTitle} € ${periodsubtitle}`;

        if (this._controls) {
            svg.attr('aria-hidden', 'true');
        }

        svg.attr('aria-labelledby', `a11y-title-${this._state.id}`)
            .attr('alt', `${this._state.chart.title || ''} ${periodTitle} € ${periodsubtitle}`);

        svg.append('title')
            .attr('id', `a11y-title-${this._state.id}`)
            .text(this._state.chart.title);


        // Generate slices container
        this._state.slices = this._state.svg.append('g');
        this._render();

        // Generate center labels
        this._state.labelWrapper = this._state.svg.append('g')
            .attr('text-anchor', 'middle');
        this._state.pillLabel = this._state.labelWrapper.append('rect');
        this._state.labels = this._state.labelWrapper.append('text');

        this._createLabels();
        this._toggleSvg(true);

        //  Generate slices legends container
        this._state.legends = this._state.svg.append('g')
            .attr('class', 'legends');
        this._createLegends();
    }

    /**
     * @example
     * // return 'AOW, 11%'
     * Creates a string that can serve as an aria-label per slice in the pie-chart
     * @param {number} index
     * @returns {String}
     */
    _getLabelForSlice(index) {
        return `${this._state.charts[this._state.index].legendItems[index]}, ${this._state.charts[this._state.index].data[index]}%`;
    }

    /**
     * Main render function
     *
     * @memberof Chart
     */

    _render() {
        // Add any new paths
        this._state.slices.datum(this._state.chart.data).selectAll('path')
            .data(this._state.pie)
            .enter()
            .append('path')
            .attr('tabindex', () => {
                if (this._controls) {
                    return null;
                }
                return 0;
            })
            .attr('aria-label', (d, index) => this._getLabelForSlice(index))
            .attr('role', 'img')
            .attr('stroke', 'white')
            .style('stroke-width', '4px')
            .attr('class', 'piechart')
            .attr('fill', (d, index) => {
                // The first colour, darkblue
                if (index === 0) {
                    return '#253280';
                }
                // The even colour lightblue
                if (index % 2 === 0) {
                    return '#D17272';
                }

                // Odd numbers will be a red, because this is complementary on blue
                // These colours have enough contrast with eachother & white
                return '#5598D1';
            })
            .attr('d', this._state.arc);


        // Add listeners
        this._state.slices.datum(this._state.chart.data).selectAll('path')
            // eslint-disable-next-line no-return-assign
            .each(d => {
                d.outerRadius = this._options.outerRadius - 10;
                this._current = d;
            })
            .on('focus', d => this._pathHover(d.index))
            .on('blur', d => this._pathOut(d.index))
            .on('mouseover', d => this._pathHover(d.index))
            .on('mouseout', d => this._pathOut(d.index));
    }

    /**
     * Generate the arcs
     *
     * @private
     */
    _switchChartData() {
        this._state.svg.attr('class', '');
        this._state.chart = this._state.charts[this._state.index];
        this._toggleSvg(false);
    }

    /**
     * Handle window resizing
     *
     * @private
     */
    _handleResize() {
        this._setFontSize();
        this._setTooltipSizes();
    }

    /**
     * Set the current font size
     *
     * @private
     */
    _setFontSize() {
        const element = this._element.querySelector('.legends');
        this._state.fontsize = parseFloat(window.getComputedStyle(element).fontSize);
    }

    /**
     * Set the width and position of each tooltip background
     *
     * @private
     */
    _setTooltipSizes() {
        if (!this._state.legends) return;
        const tooltips = [...this._state.legends.node().querySelectorAll('g')];
        tooltips && tooltips.forEach(tooltip => {
            const text = tooltip.querySelector('text');
            const rect = tooltip.querySelector('rect');
            if (!rect || !text) return;
            const bubble = tooltip.querySelector('.bubble');
            bubble.setAttribute('width', 28);
            const bubbleBox = bubble.getBBox();
            const bubbleBoxX = bubbleBox.x;
            const bubbleBoxWidth = bubbleBox.width / 2;
            const textBox = text.getBBox();
            const offset = this._state.fontsize * 2;
            const width = textBox.width + offset;
            rect.setAttribute('width', width);
            rect.setAttribute('x', -width / 2);
            bubble.setAttribute('transform', `translate(${-bubbleBoxX - bubbleBoxWidth},-26.3)`);
        });
    }

    /**
     * Handle hovered paths
     *
     * @private
     * @param {number} index Index of the path
     */

    _pathHover(index) {
        const pathOver = this._state.slices.selectAll('path').filter((d, i) => i === index);
        this._state.slices.attr('class', 'out');
        pathOver && pathOver.attr('class', 'hover');
        // Hide legends if the current legend is in a small slice
        this._state.legends && pathOver && pathOver.attr(d => this._state.legends.attr('data-hide-legends', d.value < 10));

        d3.selectAll('.legend')
            .attr('data-hover', false);

        //  Show hover / out classes on legends
        const currentLegend = this._state.legends.node().querySelector(`[index="${index}"]`);
        currentLegend.setAttribute('data-hover', true);

        const periodTitle = this._state.monthly ? this._state.chart.labels.title.monthly : this._state.chart.labels.title.yearly;
        const periodsubtitle = this._state.monthly ? this._state.chart.labels.subtitle.monthly : this._state.chart.labels.subtitle.yearly;
        this._state.descriptionText = `${this._state.chart.descriptionTotal} ${periodTitle} € ${periodsubtitle}`;

        this._state.arc.outerRadius(this._options.outerRadius);
        this._state.chart.controlLabel[index] && this._state.chart.controlLabel[index].classList.add('hover');
        const legend = this._state.legends.selectAll('text').filter((d, i) => i === index);
        legend && legend.transition()
            .duration(500)
            .delay(0)
            .style('opacity', 1.0);

        pathOver && pathOver.transition()
            .duration(500)
            .delay(0)
            .attr('d', this._state.arc);
    }

    /**
     * Reinitialize hovered paths
     *
     * @private
     * @param {number} index Index of the path
     */

    _pathOut(index) {
        const pathOut = this._state.slices.selectAll('path').filter((d, i) => i === index);
        this._state.slices.attr('class', '');
        this._state.legends.attr('data-hide-legends', false);
        this._state.arc.outerRadius(this._options.outerRadius - 10);
        this._state.chart.controlLabel[index] && this._state.chart.controlLabel[index].classList.remove('hover');
        const legends = this._state.legends.node().querySelector('[data-hover="true"]');
        legends && legends.setAttribute('data-hover', false);

        pathOut.transition()
            .delay(0)
            .duration(500)
            .attr('d', this._state.arc);

        d3.selectAll('.legends text')
            .join('text')
            .attr('class', '');
    }

    /**
     * Generate the arcs
     *
     * @memberof Chart
     */

    _createLabels() {
        // Generate center labels
        const title = this._state.monthly ? this._state.chart.labels.title.monthly : this._state.chart.labels.title.yearly;
        const subtitle = this._state.monthly ? this._state.chart.labels.subtitle.monthly : this._state.chart.labels.subtitle.yearly;
        const expect = this._state.chart.labels.expect;
        const from = this._state.chart.labels.from;
        const isLarge = title.toString().length > 4;
        this._state.labels
            .selectAll('tspan')
            .remove();
        this._state.labels.call(tspan => tspan.append('tspan')
            .attr('font-weight', 'normal')
            .attr('class', 'currency')
            .attr('x', isLarge ? '-3.35em' : '-3em')
            .attr('y', '-0.75em')
            .text('€ '))
            .call(text => text.attr('aria-hidden', true)
                .append('tspan')
                .attr('aria-hidden', true)
                .attr('x', 0)
                .attr('y', '-3.2em')
                .attr('class', 'subtitle')
                .text(expect))
            .call(text => text.attr('aria-hidden', true)
                .append('tspan')
                .attr('aria-hidden', true)
                .attr('x', 0)
                .attr('y', isLarge ? '-0.1em' : '-0.1em')
                .attr('font-weight', 'bold')
                .attr('class', 'title')
                .attr('data-size', isLarge ? 'large' : 'regular')
                .text(title))
            .call(text => text.attr('aria-hidden', true)
                .append('tspan')
                .attr('aria-hidden', true)
                .attr('x', 0)
                .attr('y', '1.5em')
                .attr('class', 'subtitle')
                .text(subtitle))
            .call(textx => textx.attr('aria-hidden', true)
                .append('tspan')
                .attr('aria-hidden', true)
                .attr('x', 0)
                .attr('y', '4.5em')
                .attr('class', 'pill-text')
                .text(from));

        this._state.pillLabel
            .selectAll('.pill')
            .remove();

        if (from && from !== '') {
            this._state.pillLabel.call(pill => pill.attr('aria-hidden', true)
                .attr('x', -50)
                .attr('y', '2.3em')
                .attr('width', 100)
                .attr('height', 30)
                .attr('rx', 15)
                .attr('ry', 15)
                .attr('class', 'pill'));
        }
    }

    /**
     * Generate the legends
     *
     * @memberof Chart
     */

    _createLegends() {

        if (this._options.showLegends !== 'true') return;
        this._setFontSize();
        this._state.legends
            .selectAll('text')
            .remove();

        this._state.legends
            .selectAll('tspan')
            .remove();

        this._state.legends.datum(this._state.chart.data)
            .selectAll('g')
            .data(this._state.pie)
            .enter()
            .append('g')
            .attr('class', 'legend')
            // .attr('opacity', 0)
            .append('rect')
            .attr('rx', '5')
            .attr('ry', '5')
            .attr('height', 22)
            .attr('class', 'rect')
            .attr('fill', '#3C3C3C')
            .attr('y', -11)
            .attr('opacity', 0.9);

        this._state.legends.attr('text-anchor', 'middle')
            .selectAll('.legend')
            .append('g')
            .attr('class', 'bubble')
            .attr('opacity', 0.9);

        this._state.legends.attr('text-anchor', 'middle')
            .selectAll('.bubble')
            .append('path')
            .attr('fill', '#3C3C3C')
            .attr('d', this._options.bubblePath);

        this._state.legends.attr('text-anchor', 'middle')
            .selectAll('.legend')
            .append('text')
            .attr('aria-hidden', true)
            .datum(this._state.chart.legendItems)
            .join('text')
            .call(text => text.append('tspan')
                .attr('y', 5)
                .text((d, index) => this._state.chart.legendItems[index]))

            .call(text => text.filter(d => d.endAngle - d.startAngle > 0.25).append('tspan')
                .attr('x', 0)
                .attr('y', '1.2em')
                .text(d => `${d.value}${this._options.unit}`))
            // eslint-disable-next-line no-confusing-arrow
            .call(text => text.attr('data-size', d => d.value < 10 ? 'small' : 'regular'));

        this._state.legends.attr('text-anchor', 'middle')
            .datum(this._state.chart.data)
            .selectAll('.legend')
            .data(this._state.pie)
            .attr('transform', d => `translate(${this._state.arc.centroid(d)})`)
            .attr('index', (d, index) => index);

        // Remove data not being used
        this._state.legends.selectAll('text')
            .attr('aria-hidden', true)
            .datum(this._state.chart.legendItems)
            .exit()
            .remove();
        this._state.legends
            .datum(this._state.chart.legendItems)
            .selectAll('tspan')
            .exit()
            .remove();

        this._setTooltipSizes();
    }


    /**
     * Setup mutation observer for the monthly/yearly price
     * @private
     */

    _setupMutationObserver() {
        const callback = mutationsList => {
            mutationsList.forEach(mutation => {
                const isMonthlyAttributeChanged =
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'data-monthly';

                const selfAttributeChanged = mutation.type === 'attributes' &&
                    mutation.attributeName === 'data-self';

                if (isMonthlyAttributeChanged) {
                    this._state.monthly =
                        this._elements.body.getAttribute('data-monthly') === 'true';
                    this._createLabels();
                }

                if (selfAttributeChanged) {
                    this._handleResize();
                }
            });
        };

        const _observer = new MutationObserver(callback);
        _observer.observe(this._elements.body, { attributes: true });
    }

    /**
     * Create toggle timeout
     * @private
     * @param {boolean} isLoaded
     */

    _toggleSvg(isLoaded) {
        setTimeout(() => this._toggleSvgCallback(isLoaded, this), 300);
    }

    /**
     * Handle SVG visibility
     * @private
     * @param {boolean} isLoaded
     * @param {*} timeout
     */

    _toggleSvgCallback(isLoaded, timeout) {
        isLoaded && this._state.svg.attr('class', 'loaded');
        !isLoaded && this._state.svg.remove();
        !isLoaded && this._initializeChart();
        clearTimeout(timeout);
    }
}

export default Chart;
/* eslint-enable */
